import type { QueryClient } from '@tanstack/react-query';
import type { CvRevisionSource } from './cv-write-coordinator';

/**
 * The latest CV revision this client has seen, kept in the React Query cache
 * (B02) so it is dropped with everything else when the account changes
 * (`queryClient.clear()` in AuthProvider).
 *
 * Its own key prefix, not `['cv', id, ...]`: CV invalidation must not touch
 * it, and it has no fetcher. It is never garbage-collected while the app
 * runs: an editor can stay open longer than the default cache time, and a
 * lost revision would silently turn its saves back into unguarded ones.
 */
export const CV_REVISION_QUERY_PREFIX = 'cv-revision';

export function cvRevisionQueryKey(target: string) {
  return [CV_REVISION_QUERY_PREFIX, target] as const;
}

const configuredClients = new WeakSet<object>();

function ensureRevisionDefaults(queryClient: QueryClient) {
  if (configuredClients.has(queryClient)) return;
  configuredClients.add(queryClient);
  queryClient.setQueryDefaults([CV_REVISION_QUERY_PREFIX], {
    gcTime: Infinity,
    staleTime: Infinity,
  });
}

export function queryCacheCvRevisions(queryClient: QueryClient): CvRevisionSource {
  ensureRevisionDefaults(queryClient);
  return {
    get(target) {
      const value = queryClient.getQueryData<number>(cvRevisionQueryKey(target));
      return typeof value === 'number' ? value : undefined;
    },
    set(target, revision) {
      // Revisions only move forward; an older answer never rolls it back.
      queryClient.setQueryData<number>(cvRevisionQueryKey(target), (previous) =>
        typeof previous === 'number' && previous >= revision ? previous : revision,
      );
    },
  };
}
